import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuditLogs, type AuditLogFilters } from "@/hooks/useAuditLogs";
import { useMe } from "@/hooks/useMe";
import { PageHeader } from "@/components/PageHeader";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Eye } from "lucide-react";
import type { AuditLog } from "@/types";

// Manter em sincronia com ALLOWED_TABLES em api/src/functions/audit-logs.ts.
// As 4 últimas entraram em 11/ago/2026 com a migration 016 (triggers que
// faltavam em profiles, user_permissions e nas duas tabelas de dinheiro).
const TABLES = [
  "clients", "interactions", "fund_assets", "fund_asset_cashflows", "funds", "investments",
  "profiles", "user_permissions", "capital_calls", "capital_distributions",
] as const;
const ACTIONS = ["INSERT", "UPDATE", "DELETE"] as const;

export default function AuditLogs() {
  const { data: me, isLoading } = useMe();
  const navigate = useNavigate();
  const [table, setTable] = useState<string>("all");
  const [action, setAction] = useState<string>("all");
  const filters: AuditLogFilters = {
    table: table === "all" ? undefined : table,
    action: action === "all" ? undefined : (action as "INSERT" | "UPDATE" | "DELETE"),
  };
  const { data, fetchNextPage, hasNextPage, isFetchingNextPage } = useAuditLogs(filters);
  const [selected, setSelected] = useState<AuditLog | null>(null);

  if (!isLoading && me && !me.user.isAdmin) {
    navigate("/");
    return null;
  }

  const items = data?.pages.flatMap((p) => p.items) ?? [];

  return (
    <div className="min-h-screen bg-background">
      <PageHeader title="IR Jus Capital" subtitle="Audit Logs" />
      <div className="container mx-auto px-4 py-8">
        <div className="flex gap-3 mb-6">
          <Select value={table} onValueChange={setTable}>
            <SelectTrigger className="w-56"><SelectValue placeholder="Table" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All tables</SelectItem>
              {TABLES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={action} onValueChange={setAction}>
            <SelectTrigger className="w-44"><SelectValue placeholder="Action" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All actions</SelectItem>
              {ACTIONS.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        <div className="rounded-lg border border-border bg-card overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="bg-muted/50 hover:bg-muted/50">
                <TableHead>Date</TableHead>
                <TableHead>User</TableHead>
                <TableHead>Table</TableHead>
                <TableHead>Action</TableHead>
                <TableHead className="w-12" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((log) => (
                <TableRow key={log.id}>
                  <TableCell className="text-muted-foreground">
                    {new Date(log.created_at).toLocaleString("pt-BR")}
                  </TableCell>
                  <TableCell>{log.user_email ?? "—"}</TableCell>
                  <TableCell><Badge variant="outline">{log.table_name}</Badge></TableCell>
                  <TableCell>
                    <Badge
                      variant={log.action === "DELETE" ? "destructive" : log.action === "INSERT" ? "default" : "secondary"}
                    >
                      {log.action}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <Button variant="ghost" size="icon" onClick={() => setSelected(log)}>
                      <Eye className="h-4 w-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>

        {hasNextPage && (
          <div className="mt-4 flex justify-center">
            <Button
              variant="outline"
              onClick={() => fetchNextPage()}
              disabled={isFetchingNextPage}
            >
              {isFetchingNextPage ? "Loading..." : "Load more"}
            </Button>
          </div>
        )}
      </div>

      <Dialog open={!!selected} onOpenChange={() => setSelected(null)}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Log Detail</DialogTitle>
          </DialogHeader>
          {selected && (
            <div className="space-y-4 text-sm">
              <div className="grid grid-cols-2 gap-4">
                <div><span className="text-muted-foreground">Date:</span> {new Date(selected.created_at).toLocaleString("pt-BR")}</div>
                <div><span className="text-muted-foreground">User:</span> {selected.user_email ?? "—"}</div>
                <div><span className="text-muted-foreground">Table:</span> {selected.table_name}</div>
                <div><span className="text-muted-foreground">Action:</span> {selected.action}</div>
              </div>
              {selected.action === "UPDATE" ? (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <h4 className="font-semibold mb-2">Before</h4>
                    <pre className="bg-muted p-3 rounded text-xs overflow-auto max-h-96">{JSON.stringify(selected.old_data, null, 2)}</pre>
                  </div>
                  <div>
                    <h4 className="font-semibold mb-2">After</h4>
                    <pre className="bg-muted p-3 rounded text-xs overflow-auto max-h-96">{JSON.stringify(selected.new_data, null, 2)}</pre>
                  </div>
                </div>
              ) : (
                <pre className="bg-muted p-3 rounded text-xs overflow-auto max-h-96">
                  {JSON.stringify(selected.action === "INSERT" ? selected.new_data : selected.old_data, null, 2)}
                </pre>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
